/**
 * 登录页 —— 蓝星现场移动端统一入口（真实 JWT 登录）。
 * 蓝渐变头部 + 圆润登录卡片 + 系统特性说明 + 演示账号快捷填入（最底部，可整体删除）。
 */
import { Box, Typography, InputBase, ButtonBase, Alert, CircularProgress } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/store/RoleContext';
import MobileFrame from '@/components/mobile/MobileFrame';
import { loadNavCreds, saveNavCreds, clearNavCreds } from '@/utils/navCreds';
import { T } from '@/theme/tokens';

/**
 * 记住我（含密码，便利优先）—— 与仓库根 index.html 五端导航页联动：
 * 导航页经链接 hash 带入的账密种子由 App 级落入 bp_nav_creds_mobile，本页从该 key 回显。
 * LEGACY_REMEMBER_KEY 为旧版 key（仅用户名），仅用于首轮兼容回显，保存时统一迁移到新 key。
 */
const LEGACY_REMEMBER_KEY = 'bp_field_remember';

const FEATURES = [
  { icon: <Inventory2OutlinedIcon />, label: '全球集采', desc: '源头直通' },
  { icon: <PublicOutlinedIcon />, label: '智能履约', desc: '全程可视' },
  { icon: <SyncOutlinedIcon />, label: '现场协同', desc: '实时同步' },
];

/** 登录输入框：50px 高、浅蓝底，聚焦时品牌色描边 + 外发光（对齐设计稿 .field） */
function Field({
  icon,
  ...inputProps
}: {
  icon: ReactNode;
  type?: string;
  placeholder: string;
  value: string;
  autoComplete?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        height: 50,
        border: '1.5px solid #E3EAF4',
        borderRadius: T.rMd,
        px: '14px',
        mb: '14px',
        bgcolor: '#F6FAFE',
        transition: 'all .18s',
        '& > .MuiSvgIcon-root': { fontSize: 20, color: T.ink3, flexShrink: 0 },
        '&:focus-within': { borderColor: T.brand, boxShadow: `0 0 0 3px ${T.brandSoft}`, bgcolor: '#fff' },
      }}
    >
      {icon}
      <InputBase
        fullWidth
        {...inputProps}
        inputProps={{ 'aria-label': inputProps.placeholder }}
        sx={{ fontSize: '15px', color: T.ink, '& input::placeholder': { color: T.ink4, opacity: 1 } }}
      />
    </Box>
  );
}

export default function LoginPage() {
  const { login } = useRole();
  const navigate = useNavigate();

  // 初始化：记住我（含密码）—— 优先读新 key；旧 key（仅用户名）兼容回显首轮
  const [username, setUsername] = useState(() => {
    const saved = loadNavCreds();
    if (saved?.u) return saved.u;
    try {
      const legacy = localStorage.getItem(LEGACY_REMEMBER_KEY);
      const parsed = legacy ? JSON.parse(legacy) : null;
      return parsed?.username || '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState(() => loadNavCreds()?.p ?? '');
  const [remember, setRemember] = useState(() => {
    if (loadNavCreds()?.u) return true;
    try {
      const legacy = localStorage.getItem(LEGACY_REMEMBER_KEY);
      return legacy ? Boolean(JSON.parse(legacy).username) : false;
    } catch {
      return false;
    }
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async () => {
    setError('');
    setNotice('');
    if (!username.trim() || !password.trim()) {
      setError('请输入账号和密码');
      return;
    }
    setSubmitting(true);
    const ok = await login(username.trim(), password.trim());
    setSubmitting(false);
    if (ok) {
      // 记住我（含密码，便利优先）：勾选则落本端 localStorage 并清理旧 key；取消则全清
      if (remember) {
        saveNavCreds(username.trim(), password.trim());
      } else {
        clearNavCreds();
      }
      try {
        localStorage.removeItem(LEGACY_REMEMBER_KEY);
      } catch {
        /* ignore */
      }
      navigate('/mobile/home', { replace: true });
    } else {
      setError('账号或密码错误');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <MobileFrame>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: '26px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(120% 75% at 88% -10%, rgba(135,180,255,.20), rgba(135,180,255,0) 55%),
            radial-gradient(90% 55% at -6% 104%, rgba(195,216,255,.24), rgba(195,216,255,0) 55%),
            linear-gradient(170deg,#F4F9FE 0%,#F9FCFF 52%,#FFFFFF 100%)`,
          // 柔光球：制造通透层次但不抢戏
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            top: -96,
            right: -72,
            background: 'radial-gradient(circle at 36% 34%, rgba(125,175,255,.34), rgba(125,175,255,.08) 58%, rgba(125,175,255,0) 72%)',
            filter: 'blur(4px)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 220,
            height: 220,
            borderRadius: '50%',
            bottom: -90,
            left: -80,
            background: 'radial-gradient(circle, rgba(150,205,255,.24), rgba(150,205,255,0) 70%)',
            filter: 'blur(4px)',
            pointerEvents: 'none',
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        {/* 品牌区 */}
        <Box sx={{ mt: { xs: '56px', sm: '64px' }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            component="img"
            src="/logo-bluplanet.png"
            alt="Bluplanet"
            sx={{ width: 74, height: 74, objectFit: 'contain', display: 'block' }}
          />
          <Typography sx={{ mt: '18px', color: T.ink, fontSize: '23px', fontWeight: 700, letterSpacing: '.02em' }}>
            蓝星履约平台
          </Typography>
          <Typography sx={{ mt: '6px', color: T.ink3, fontSize: '12px', fontWeight: 600, letterSpacing: '.18em' }}>
            BLUPLANET · 现场作业端
          </Typography>
          <Box
            sx={{
              mt: '14px',
              fontSize: '13px',
              color: T.brand,
              bgcolor: T.brandSoft,
              px: '14px',
              py: '6px',
              borderRadius: T.rPill,
              border: `1px solid ${T.brandLine}`,
            }}
          >
            全球采购履约网络
          </Box>
        </Box>

        {/* 登录卡片（毛玻璃） */}
        <Box
          sx={{
            mt: '34px',
            width: '100%',
            background: 'linear-gradient(180deg,rgba(255,255,255,.82),rgba(244,249,255,.66))',
            border: '1px solid rgba(31,111,229,.14)',
            borderRadius: T.rLg,
            px: '20px',
            py: '22px',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow: '0 20px 46px rgba(31,60,120,.12), inset 0 1px 0 rgba(255,255,255,.75)',
          }}
        >
          {(error || notice) && (
            <Alert
              severity={error ? 'error' : 'info'}
              sx={{ mb: 1.75, fontSize: '12.5px', borderRadius: T.rSm, py: 0 }}
            >
              {error || notice}
            </Alert>
          )}

          <Field
            icon={<PersonOutlineIcon />}
            placeholder="请输入工作账号"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <Field
            icon={<LockOutlinedIcon />}
            type="password"
            placeholder="请输入密码"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: '-2px', mx: '2px', mb: '18px' }}>
            <ButtonBase
              role="checkbox"
              aria-checked={remember}
              onClick={() => setRemember((v) => !v)}
              sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: T.ink2, fontFamily: 'inherit' }}
            >
              <Box
                component="span"
                sx={{
                  width: 17,
                  height: 17,
                  borderRadius: '5px',
                  border: `1.5px solid ${remember ? T.brand : T.ink4}`,
                  bgcolor: remember ? T.brand : '#fff',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all .15s',
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 13, opacity: remember ? 1 : 0 }} />
              </Box>
              记住我
            </ButtonBase>
            <ButtonBase
              onClick={() => {
                setError('');
                setNotice('请联系公司运营为你重置密码');
              }}
              sx={{ fontSize: '13px', color: T.brand, fontWeight: 600, fontFamily: 'inherit' }}
            >
              忘记密码？
            </ButtonBase>
          </Box>

          <ButtonBase
            disabled={submitting}
            onClick={handleLogin}
            sx={{
              width: '100%',
              height: 50,
              borderRadius: T.rMd,
              background: T.brandGrad,
              color: '#fff',
              fontFamily: 'inherit',
              fontSize: '16px',
              fontWeight: 800,
              letterSpacing: '.04em',
              gap: 1,
              boxShadow: '0 10px 24px rgba(31,111,229,.40), inset 0 1px 0 rgba(255,255,255,.35)',
              transition: 'transform .16s, opacity .16s',
              '&:active': { transform: 'translateY(1px)' },
              '&.Mui-disabled': { opacity: 0.75 },
            }}
          >
            {submitting && <CircularProgress size={18} sx={{ color: '#fff' }} />}
            {submitting ? '登录中...' : '登 录'}
          </ButtonBase>
        </Box>

        {/* 核心能力 */}
        <Box sx={{ mt: '30px', width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '11px' }}>
          {FEATURES.map((f) => (
            <Box
              key={f.label}
              sx={{
                bgcolor: '#fff',
                border: `1px solid ${T.line}`,
                borderRadius: T.rMd,
                py: '14px',
                px: '10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: T.shadowSm,
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  bgcolor: T.brandSoft,
                  color: T.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: '9px',
                  '& .MuiSvgIcon-root': { fontSize: 19 },
                }}
              >
                {f.icon}
              </Box>
              <Typography sx={{ color: T.ink, fontSize: '13px', fontWeight: 700 }}>{f.label}</Typography>
              <Typography sx={{ color: T.ink3, fontSize: '10.5px', mt: '3px', lineHeight: 1.3 }}>{f.desc}</Typography>
            </Box>
          ))}
        </Box>

        {/* 底部 */}
        <Typography sx={{ mt: 'auto', pt: 3, mb: '22px', color: T.ink3, fontSize: '11px' }}>
          蓝星履约平台 · 全球农产品数智履约服务
        </Typography>
      </Box>
    </MobileFrame>
  );
}
